/**
 * Dashboard Page - Refactored (Phase 8.2)
 * Quick glance at business health with key performance indicators
 *
 * Layout:
 * - Health at a Glance (2 traffic lights: Collections + Pipeline)
 * - Key Stats row
 * - What Needs Attention (consolidated action items + ball-in-court)
 * - Financial & Action Widgets (9.9b)
 * - Tax Estimates (R1 GST Remittance + R2 Corp Tax + R3 Draft Invoices)
 * - Revenue Trend + Projects by Stage charts (collapsed by default - 9.9f)
 * - Link to full /reports page
 *
 * 9.9f: Added collapsible sections with localStorage persistence
 * 9.9b: Added FinancialSummary, Reminders, AttentionCentre widgets
 * R1/R2/R3: Added GstRemittance, CorpTax, DraftInvoices widgets
 */
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { HealthIndicators } from '@/components/dashboard/HealthIndicators';
import { DashboardStatsCards } from '@/components/reports/DashboardStatsCards';
import { RevenueGoalWidget } from '@/components/dashboard/RevenueGoalWidget';
import { UpcomingTasks } from '@/components/dashboard/UpcomingTasks';
import { FinancialSummaryWidget } from '@/components/dashboard/FinancialSummaryWidget';
import { MonthlyProposalActivityWidget } from '@/components/dashboard/MonthlyProposalActivityWidget';
import { RemindersWidget } from '@/components/dashboard/RemindersWidget';
import { AttentionCentreWidget } from '@/components/dashboard/AttentionCentreWidget';
import { GstRemittanceWidget } from '@/components/dashboard/GstRemittanceWidget';
import { CorpTaxWidget } from '@/components/dashboard/CorpTaxWidget';
import { DraftInvoicesWidget } from '@/components/dashboard/DraftInvoicesWidget';
import { UpcomingFollowupsWidget } from '@/components/dashboard/UpcomingFollowupsWidget';
import { RevenueChart } from '@/components/reports/RevenueChart';
import { ProjectsStageChart } from '@/components/reports/ProjectsStageChart';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDashboardPreferences } from '@/hooks/useDashboardPreferences';
import { BarChart3, Activity, TrendingUp, ListTodo, LineChart, PieChart, DollarSign, Receipt, Bell, FileBarChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { OnboardingChecklistCard } from '@/components/onboarding/OnboardingChecklist';

export function DashboardPage() {
  const { isWidgetExpanded, setWidgetExpanded } = useDashboardPreferences();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Onboarding Checklist — Phase 10f (self-managing visibility) */}
        <OnboardingChecklistCard />

        {/* Page Header - always visible */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Quick glance at your business health
            </p>
          </div>
          <Link
            to="/reports"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-colors text-sm font-medium"
          >
            <BarChart3 size={18} />
            View All Reports
          </Link>
        </div>

        {/* Top Row: Health Indicators + Stats - Expanded by default */}
        <CollapsibleSection
          title="Health at a Glance"
          icon={<Activity className="w-5 h-5 text-slate-600" />}
          open={isWidgetExpanded('health-indicators')}
          onOpenChange={(open) => setWidgetExpanded('health-indicators', open)}
          testId="section-health"
          summary="Key health indicators"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4" data-testid="health-indicators">
            <HealthIndicators />
            <DashboardStatsCards />
          </div>
        </CollapsibleSection>

        {/* Revenue Goal Progress (Phase 9.5b) - Expanded by default */}
        <CollapsibleSection
          title="Revenue Goal Progress"
          icon={<TrendingUp className="w-5 h-5 text-emerald-600" />}
          open={isWidgetExpanded('revenue-goal')}
          onOpenChange={(open) => setWidgetExpanded('revenue-goal', open)}
          testId="section-revenue-goal"
          summary="Monthly/yearly progress"
        >
          <RevenueGoalWidget />
        </CollapsibleSection>

        {/* What Needs Attention - Expanded by default */}
        <CollapsibleSection
          title="What Needs Attention"
          icon={<ListTodo className="w-5 h-5 text-amber-600" />}
          open={isWidgetExpanded('upcoming-tasks')}
          onOpenChange={(open) => setWidgetExpanded('upcoming-tasks', open)}
          testId="section-upcoming-tasks"
          summary="Action items"
        >
          <UpcomingTasks maxBallInCourtItems={5} />
        </CollapsibleSection>

        {/* Financial & Action Widgets Row (9.9b) - Expanded by default */}
        <CollapsibleSection
          title="Financial Overview"
          icon={<DollarSign className="w-5 h-5 text-emerald-600" />}
          open={isWidgetExpanded('financial-summary')}
          onOpenChange={(open) => setWidgetExpanded('financial-summary', open)}
          testId="section-financial"
          summary="Proposals, contracts, revenue"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <FinancialSummaryWidget />
            <RemindersWidget maxItems={5} />
            <AttentionCentreWidget />
          </div>
        </CollapsibleSection>

        {/* Monthly Proposal Activity (SPEC #151) - Expanded by default */}
        <CollapsibleSection
          title="Monthly Proposal Activity"
          icon={<FileBarChart className="w-5 h-5 text-indigo-600" />}
          open={isWidgetExpanded('proposal-activity')}
          onOpenChange={(open) => setWidgetExpanded('proposal-activity', open)}
          testId="section-proposal-activity"
          summary="Prepared vs won, last 12 months"
        >
          <MonthlyProposalActivityWidget />
        </CollapsibleSection>

        {/* Tax Estimates + Draft Invoices (R1/R2/R3) - Expanded by default */}
        <CollapsibleSection
          title="Tax Estimates & Drafts"
          icon={<Receipt className="w-5 h-5 text-blue-600" />}
          open={isWidgetExpanded('tax-estimates')}
          onOpenChange={(open) => setWidgetExpanded('tax-estimates', open)}
          testId="section-tax-estimates"
          summary="GST, corp tax, draft invoices"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <GstRemittanceWidget />
            <CorpTaxWidget />
            <DraftInvoicesWidget />
          </div>
        </CollapsibleSection>

        {/* Collection Follow-ups (R4) - Expanded by default */}
        <CollapsibleSection
          title="Collection Follow-ups"
          icon={<Bell className="w-5 h-5 text-blue-600" />}
          open={isWidgetExpanded('collection-followups')}
          onOpenChange={(open) => setWidgetExpanded('collection-followups', open)}
          testId="section-collection-followups"
          summary="Pending collection reminders"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <UpcomingFollowupsWidget />
          </div>
        </CollapsibleSection>

        {/* Charts Row - COLLAPSED by default */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CollapsibleSection
            title="Revenue Trend"
            icon={<LineChart className="w-5 h-5 text-slate-500" />}
            open={isWidgetExpanded('revenue-chart')}
            onOpenChange={(open) => setWidgetExpanded('revenue-chart', open)}
            testId="section-revenue-chart"
            summary="Last 6 months"
          >
            <RevenueChart months={6} />
          </CollapsibleSection>

          <CollapsibleSection
            title="Projects by Stage"
            icon={<PieChart className="w-5 h-5 text-slate-500" />}
            open={isWidgetExpanded('projects-stage-chart')}
            onOpenChange={(open) => setWidgetExpanded('projects-stage-chart', open)}
            testId="section-projects-stage"
            summary="Pipeline distribution"
          >
            <ProjectsStageChart />
          </CollapsibleSection>
        </div>

        {/* Call to Action - Link to Full Reports - always visible */}
        <div className="bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Need deeper insights?
          </h3>
          <p className="text-gray-700 mb-4">
            View all reports including business metrics and cash flow forecasting.
          </p>
          <Link
            to="/reports"
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-colors font-medium"
          >
            <BarChart3 size={20} />
            Go to Full Reports Page
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
}
